import moment from 'moment';
import isValidDate from './isValidDate';

const MONTHS_NOMINATIVE = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function toUtcMoment(value) {
  const m = moment.utc(value);
  return m.isValid() ? m : null;
}

function formatDayRanges(days) {
  if (!days.length) return '';

  const parts = [];
  let rangeStart = days[0];
  let prev = days[0];

  for (let i = 1; i < days.length; i += 1) {
    const day = days[i];
    if (day === prev + 1) {
      prev = day;
      continue;
    }
    parts.push(rangeStart === prev ? `${rangeStart}` : `${rangeStart}–${prev}`);
    rangeStart = day;
    prev = day;
  }

  parts.push(rangeStart === prev ? `${rangeStart}` : `${rangeStart}–${prev}`);
  return parts.join(', ');
}

function extractSharedTime(moments) {
  for (let i = 0; i < moments.length; i += 1) {
    const m = moments[i];
    if (m.hours() || m.minutes() || m.seconds() || m.milliseconds()) {
      return m.format('HH:mm');
    }
  }
  return '';
}

/**
 * Сжимает массив дат в строку вида:
 * "2, 7, 20, 26–27 февраль 2026, 7 ноябрь 2026, 16 май 2027"
 * Если у дат есть время (не полночь UTC) — добавляет его в конец.
 */
export function formatHoldingDatesList(dates) {
  if (!Array.isArray(dates) || !dates.length) return '';

  const moments = dates
    .map(toUtcMoment)
    .filter(Boolean)
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (!moments.length) return '';

  const groups = [];
  let currentKey = null;
  let currentDays = [];

  moments.forEach((m) => {
    const key = `${m.year()}-${m.month()}`;
    if (key !== currentKey) {
      if (currentKey !== null) {
        groups.push({ key: currentKey, days: currentDays });
      }
      currentKey = key;
      currentDays = [m.date()];
      return;
    }
    if (currentDays[currentDays.length - 1] !== m.date()) {
      currentDays.push(m.date());
    }
  });

  if (currentKey !== null) {
    groups.push({ key: currentKey, days: currentDays });
  }

  const formatted = groups.map(({ key, days }) => {
    const [yearStr, monthStr] = key.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    return `${formatDayRanges(days)} ${MONTHS_NOMINATIVE[month]} ${year}`;
  }).join(', ');

  const time = extractSharedTime(moments);
  return time ? `${formatted}, ${time}` : formatted;
}

/**
 * Для карточки: список дат, иначе легаси holding_date.
 */
export function formatEventHoldingDates(event) {
  const list = event?.holding_dates_list;
  if (Array.isArray(list) && list.length > 0) {
    return formatHoldingDatesList(list);
  }
  const legacy = event?.holding_date;
  return legacy != null && String(legacy).trim() !== '' ? String(legacy).trim() : '';
}

export function isValidHoldingTime(time) {
  if (!time) return true;
  return TIME_RE.test(time);
}

/**
 * Разбирает API-массив в поля формы: даты YYYY-MM-DD и общее время HH:mm.
 */
export function parseHoldingDatesListForForm(list = []) {
  if (!Array.isArray(list) || !list.length) {
    return { dates: [''], time: '' };
  }

  const moments = list
    .map(toUtcMoment)
    .filter(Boolean)
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (!moments.length) {
    return { dates: [''], time: '' };
  }

  const dates = [];
  moments.forEach((m) => {
    const day = m.format('YYYY-MM-DD');
    if (!dates.includes(day)) dates.push(day);
  });

  return {
    dates,
    time: extractSharedTime(moments),
  };
}

/**
 * Собирает payload для API из дат формы и опционального времени.
 */
export function buildHoldingDatesListPayload(dates = [], time = '') {
  const uniqueDays = [];
  dates.forEach((day) => {
    if (!day || uniqueDays.includes(day)) return;
    const parsed = moment.utc(day, 'YYYY-MM-DD', true);
    if (parsed.isValid() && isValidDate(parsed.toDate())) {
      uniqueDays.push(day);
    }
  });

  uniqueDays.sort();

  if (!uniqueDays.length) return [];

  if (time && TIME_RE.test(time)) {
    return uniqueDays.map((day) => moment.utc(`${day}T${time}:00.000`).toISOString());
  }

  return uniqueDays;
}
